/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	type Context,
	type FocusEvent,
	type KeyboardEvent,
	type MouseEvent,
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from 'react';

import { jsx, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ButtonItem from '@atlaskit/menu/button-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { RemixHiddenOptionKind } from '../../../types';

import { SUB_TYPE_ICON_MAP } from './constants';
import messages from './messages';
import type { KnownSubType, RemixOption, RemixOptionKind, RemixSubType } from './types';
import { getRemixMenuOptions } from './utils';

export type CascadeFlyoutDirection = 'left' | 'right';
export type CascadeVerticalDirection = 'down' | 'up';

export const CascadeVerticalDirectionContext: Context<CascadeVerticalDirection> =
	createContext<CascadeVerticalDirection>('down');

export function getCascadeVerticalDirectionForPlacement(
	placement: string | undefined,
): CascadeVerticalDirection {
	return placement?.startsWith('top') ? 'up' : 'down';
}

// Width of a menu column (top-level categories and sub-type submenu).
const CATEGORY_WIDTH = 240;
const SUBTYPE_WIDTH = 160;
const PREVIEW_WIDTH = 176;
const PREVIEW_HEIGHT = 128;
const CASCADE_COLUMN_GAP = 6;
const CASCADE_VIEWPORT_MARGIN = 8;
const CASCADE_FLYOUT_WIDTH = SUBTYPE_WIDTH + PREVIEW_WIDTH + CASCADE_COLUMN_GAP * 2;

export function getCascadeDirectionForAnchor(
	anchorRect: DOMRect,
	viewportWidth: number,
): CascadeFlyoutDirection {
	const fullCascadeWidth = CATEGORY_WIDTH + CASCADE_FLYOUT_WIDTH;
	const availableRightForFullCascade = viewportWidth - anchorRect.left - CASCADE_VIEWPORT_MARGIN;
	const availableLeftForFlyout = anchorRect.left - CASCADE_VIEWPORT_MARGIN;

	return availableRightForFullCascade < fullCascadeWidth &&
		availableLeftForFlyout >= CASCADE_FLYOUT_WIDTH
		? 'left'
		: 'right';
}

const subtleSlideIn = keyframes({
	from: { opacity: 0, transform: 'translateX(-8px)' },
	to: { opacity: 1, transform: 'translateX(0)' },
});

const menuStyles = cssMap({
	popupEnterAnimation: {
		animationName: subtleSlideIn,
		animationDuration: '200ms',
		animationTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
		animationFillMode: 'backwards',
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
		},
	},
	// Keep every layer in normal flow so ADS Popup measures/renders the whole cascade.
	// In left mode, Popup uses bottom-end placement and row-reverse keeps the category
	// column by the trigger while subtype/preview columns appear to its left.
	rootRightDown: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: token('space.075'),
	},
	rootLeftDown: {
		display: 'flex',
		flexDirection: 'row-reverse',
		alignItems: 'flex-start',
		gap: token('space.075'),
	},
	rootRightUp: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-end',
		gap: token('space.075'),
	},
	rootLeftUp: {
		display: 'flex',
		flexDirection: 'row-reverse',
		alignItems: 'flex-end',
		gap: token('space.075'),
	},
	categoryColumn: {
		width: `${CATEGORY_WIDTH}px`,
		flex: '0 0 auto',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
	},
	categoryHeader: {
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.100'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
	},
	subTypeColumn: {
		width: `${SUBTYPE_WIDTH}px`,
		flex: '0 0 auto',
		overflow: 'hidden',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
	},
	previewColumnRight: {
		width: `${PREVIEW_WIDTH}px`,
		height: `${PREVIEW_HEIGHT}px`,
		flex: '0 0 auto',
		alignSelf: 'auto',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.100'),
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		overflow: 'hidden',
		boxSizing: 'border-box',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
		transitionProperty: 'transform',
		transitionDuration: '150ms',
		transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	previewColumnLeft: {
		width: `${PREVIEW_WIDTH}px`,
		height: `${PREVIEW_HEIGHT}px`,
		flex: '0 0 auto',
		alignSelf: 'auto',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.100'),
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		overflow: 'hidden',
		boxSizing: 'border-box',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
		transitionProperty: 'transform',
		transitionDuration: '150ms',
		transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	// Trailing chevron for a category row that opens a submenu.
	categoryTrailing: {
		display: 'flex',
		alignItems: 'center',
	},
	previewContent: {
		width: '100%',
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		pointerEvents: 'none',
	},
});

interface PreviewOffset {
	blockStart: number;
	blockEnd: number;
}

interface RemixSubTypeColumnProps {
	subTypes: RemixSubType[];
	categoryOffset: PreviewOffset;
	pickForMeTestId: string;
	onSubTypeSelect: (subTypeValue: string) => void;
	onPickForMe: () => void;
	onClearPreview: () => void;
	onHoverSubType: (subTypeValue: string, previewOffset: PreviewOffset) => void;
}

function RemixSubTypeColumn({
	subTypes,
	categoryOffset,
	pickForMeTestId,
	onSubTypeSelect,
	onPickForMe,
	onClearPreview,
	onHoverSubType,
}: RemixSubTypeColumnProps) {
	const { formatMessage } = useIntl();
	const verticalDirection = useContext(CascadeVerticalDirectionContext);
	const columnRef = useRef<HTMLDivElement>(null);

	const handleHoverSubType = useCallback(
		(subTypeValue: string, rowElement: HTMLElement) => {
			const columnElement = columnRef.current;
			const rootElement = columnElement?.parentElement;
			if (!columnElement || !rootElement) {
				onHoverSubType(subTypeValue, { blockStart: 0, blockEnd: 0 });
				return;
			}

			const rootRect = rootElement.getBoundingClientRect();
			const rowRect = rowElement.getBoundingClientRect();
			const rowMidpoint = rowRect.top + rowRect.height / 2;
			const previewHalfHeight = PREVIEW_HEIGHT / 2;

			// Keep the preview's midpoint exactly aligned with the subtype row midpoint. These
			// offsets intentionally remain unclamped so edge-row previews may extend beyond the
			// subtype card instead of being pushed off-center.
			const blockStart = rowMidpoint - rootRect.top - previewHalfHeight;
			const blockEnd = rootRect.bottom - rowMidpoint - previewHalfHeight;
			onHoverSubType(subTypeValue, { blockStart, blockEnd });
		},
		[onHoverSubType],
	);

	return (
		<Box
			ref={columnRef}
			xcss={menuStyles.subTypeColumn}
			style={{
				transform: `translateY(${
					verticalDirection === 'up' ? -categoryOffset.blockEnd : categoryOffset.blockStart
				}px)`,
			}}
			testId="remix-menu-submenu"
		>
			<Box xcss={menuStyles.popupEnterAnimation}>
				<MenuGroup>
					<Section>
						{subTypes.map((subType) => {
							const label = formatMessage(subType.labelMessage);
							return (
								<Box
									key={subType.value}
									testId={`remix-menu-subtype-row-${subType.value}`}
									onMouseEnter={(event: MouseEvent<HTMLDivElement>) =>
										handleHoverSubType(subType.value, event.currentTarget)
									}
									onFocus={(event: FocusEvent<HTMLDivElement>) =>
										handleHoverSubType(subType.value, event.currentTarget)
									}
								>
									<ButtonItem
										testId={subType.testId ?? `remix-menu-subtype-${subType.value}`}
										onClick={() => onSubTypeSelect(subType.value)}
									>
										{label}
									</ButtonItem>
								</Box>
							);
						})}
					</Section>
				</MenuGroup>
				<MenuGroup spacing="compact">
					<Section hasSeparator>
						<Box onMouseEnter={onClearPreview} onFocus={onClearPreview}>
							<ButtonItem testId={pickForMeTestId} onClick={onPickForMe}>
								{formatMessage(messages.pickForMe)}
							</ButtonItem>
						</Box>
					</Section>
				</MenuGroup>
			</Box>
		</Box>
	);
}

function RemixPreviewColumn({
	subType,
	direction,
	previewOffset,
}: {
	subType: RemixSubType | null;
	direction: CascadeFlyoutDirection;
	previewOffset: PreviewOffset;
}) {
	const { formatMessage } = useIntl();
	const verticalDirection = useContext(CascadeVerticalDirectionContext);
	const IconComponent =
		subType && subType.value in SUB_TYPE_ICON_MAP
			? SUB_TYPE_ICON_MAP[subType.value as KnownSubType]
			: null;

	if (!IconComponent || !subType) {
		return null;
	}

	const label = formatMessage(subType.labelMessage);

	return (
		<Box
			xcss={direction === 'left' ? menuStyles.previewColumnLeft : menuStyles.previewColumnRight}
			style={{
				transform: `translateY(${
					verticalDirection === 'up' ? -previewOffset.blockEnd : previewOffset.blockStart
				}px)`,
			}}
			testId="remix-menu-preview-column"
		>
			<Box xcss={menuStyles.previewContent} testId="remix-menu-preview">
				<IconComponent alt={label} />
			</Box>
		</Box>
	);
}

interface RemixCategoryItemProps {
	option: RemixOption;
	isActive: boolean;
	onActivate: (option: RemixOption, rowElement: HTMLElement) => void;
	onFlatSelect: (type: RemixOptionKind) => void;
	renderOptionIcon: (option: RemixOption, alt: string) => JSX.Element;
}

function RemixCategoryItem({
	option,
	isActive,
	onActivate,
	onFlatSelect,
	renderOptionIcon,
}: RemixCategoryItemProps) {
	const { formatMessage } = useIntl();

	const label = formatMessage(option.labelMessage);
	const optionIconAlt = formatMessage(option.iconAltMessage);
	const hasSubTypes = option.subTypes.length > 0;

	if (!hasSubTypes) {
		return (
			<Box
				testId={`remix-menu-category-row-${option.type}`}
				onMouseEnter={(event: MouseEvent<HTMLDivElement>) =>
					onActivate(option, event.currentTarget)
				}
				onFocus={(event: FocusEvent<HTMLDivElement>) => onActivate(option, event.currentTarget)}
			>
				<ButtonItem
					iconBefore={renderOptionIcon(option, optionIconAlt)}
					testId={option.testId}
					onClick={() => onFlatSelect(option.type)}
				>
					{label}
				</ButtonItem>
			</Box>
		);
	}

	return (
		<Box
			testId={`remix-menu-category-row-${option.type}`}
			onMouseEnter={(event: MouseEvent<HTMLDivElement>) => onActivate(option, event.currentTarget)}
			onFocus={(event: FocusEvent<HTMLDivElement>) => onActivate(option, event.currentTarget)}
		>
			<ButtonItem
				isSelected={isActive}
				iconBefore={renderOptionIcon(option, optionIconAlt)}
				iconAfter={
					<Box xcss={menuStyles.categoryTrailing}>
						<ChevronRightIcon label="" color={token('color.icon')} size="small" />
					</Box>
				}
				testId={option.testId}
				onClick={(event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) =>
					onActivate(option, event.currentTarget)
				}
			>
				{label}
			</ButtonItem>
		</Box>
	);
}

export interface RemixCascadingMenuContentProps {
	onOptionSelect?: (parameters: {
		type: RemixOptionKind;
		subType?: string;
		userPrompt?: string;
	}) => void;
	options?: RemixOption[];
	hiddenOptions?: readonly RemixHiddenOptionKind[];
	flyoutDirection?: CascadeFlyoutDirection;
	showFlyout?: boolean;
	renderOptionIcon: (option: RemixOption, alt: string) => JSX.Element;
	/** Rendered above the category list within the first menu layer. */
	header?: ReactNode;
	/** Rendered below the category list, e.g. the free-text prompt row. */
	footer?: ReactNode;
}

/**
 * RemixCascadingMenuContent — lightweight cascading dropdown menu (like a toolbar /
 * right-click menu): top-level categories (Charts, Diagrams, …) each open a right-side
 * submenu of sub-types (Line, Area, …). Selecting a sub-type emits `{ type, subType }`.
 * Categories without sub-types are directly clickable and emit only `{ type }`, letting
 * the backend choose.
 *
 * This is the "inline dropdown" Remix menu variant, used by RemixPopup (anchored popup
 * presentation) as a lighter-weight alternative to RemixOverlayContent's accordion.
 */
export function RemixCascadingMenuContent({
	onOptionSelect,
	options,
	hiddenOptions,
	flyoutDirection = 'right',
	showFlyout = true,
	renderOptionIcon,
	header,
	footer,
}: RemixCascadingMenuContentProps): JSX.Element {
	const verticalDirection = useContext(CascadeVerticalDirectionContext);
	const categoryColumnRef = useRef<HTMLDivElement>(null);
	const resolvedOptions = useMemo(
		() =>
			getRemixMenuOptions({
				source:
					options === undefined
						? { kind: 'preset', preset: 'selection' }
						: {
								kind: 'custom',
								choices: options,
							},
				excludedRemixTypes: hiddenOptions ? [...hiddenOptions] : undefined,
			}),
		[hiddenOptions, options],
	);

	// The category whose sub-types are currently shown in the second column. Only
	// categories that have sub-types open a flyout; the rest close it.
	const [activeType, setActiveType] = useState<RemixOptionKind | null>(null);
	// Measured from both edges of the active category row, positioning the subtype card.
	const [categoryOffset, setCategoryOffset] = useState<PreviewOffset>({
		blockStart: 0,
		blockEnd: 0,
	});
	// The sub-type currently hovered/focused, driving the preview column.
	const [hoveredSubType, setHoveredSubType] = useState<string | null>(null);
	// Measured from both root edges so the preview center follows the subtype row center,
	// including when edge-row previews extend beyond the subtype card.
	const [previewOffset, setPreviewOffset] = useState<PreviewOffset>({
		blockStart: 0,
		blockEnd: 0,
	});

	const activeOption = useMemo(
		() => resolvedOptions.find((opt) => opt.type === activeType && opt.subTypes.length > 0) ?? null,
		[resolvedOptions, activeType],
	);
	const activeSubTypes = activeOption?.subTypes ?? [];
	const hoveredSubTypeDef = activeSubTypes.find((st) => st.value === hoveredSubType) ?? null;
	const handleActivate = useCallback((option: RemixOption, rowElement: HTMLElement) => {
		const categoryRect = categoryColumnRef.current?.getBoundingClientRect();
		const rowRect = rowElement.getBoundingClientRect();
		setCategoryOffset(
			categoryRect
				? {
						blockStart: Math.max(0, rowRect.top - categoryRect.top),
						blockEnd: Math.max(0, categoryRect.bottom - rowRect.bottom),
					}
				: { blockStart: 0, blockEnd: 0 },
		);
		setActiveType(option.subTypes.length > 0 ? option.type : null);
		setHoveredSubType(null);
		setPreviewOffset({ blockStart: 0, blockEnd: 0 });
	}, []);

	const handleHoverSubType = useCallback(
		(subTypeValue: string, nextPreviewOffset: PreviewOffset) => {
			setHoveredSubType(subTypeValue);
			setPreviewOffset(nextPreviewOffset);
		},
		[],
	);

	const handleClearPreview = useCallback(() => {
		setHoveredSubType(null);
		setPreviewOffset({ blockStart: 0, blockEnd: 0 });
	}, []);

	const handleFlatSelect = useCallback(
		(type: RemixOptionKind) => {
			onOptionSelect?.({ type });
		},
		[onOptionSelect],
	);

	const handlePickForMe = useCallback(() => {
		if (activeOption) {
			onOptionSelect?.({ type: activeOption.type });
		}
	}, [activeOption, onOptionSelect]);

	const handleSubTypeSelect = useCallback(
		(subTypeValue: string) => {
			if (!activeOption) {
				return;
			}
			if (fg('cc-maui-phase-3')) {
				const subTypeDef = activeOption.subTypes.find((st) => st.value === subTypeValue);
				onOptionSelect?.({
					type: subTypeDef?.emitType ?? activeOption.type,
					subType: subTypeValue,
				});
				return;
			}
			onOptionSelect?.({ type: activeOption.type, subType: subTypeValue });
		},
		[activeOption, onOptionSelect],
	);

	return (
		<Box
			xcss={
				flyoutDirection === 'left'
					? verticalDirection === 'up'
						? menuStyles.rootLeftUp
						: menuStyles.rootLeftDown
					: verticalDirection === 'up'
						? menuStyles.rootRightUp
						: menuStyles.rootRightDown
			}
			testId="remix-menu"
		>
			<Box ref={categoryColumnRef} xcss={menuStyles.categoryColumn} testId="remix-menu-categories">
				{header ? (
					<Box xcss={menuStyles.categoryHeader} testId="remix-menu-header">
						{header}
					</Box>
				) : null}
				<Box>
					<MenuGroup>
						<Section>
							{resolvedOptions.map((option) => (
								<RemixCategoryItem
									key={option.type}
									option={option}
									isActive={activeType === option.type}
									onActivate={handleActivate}
									onFlatSelect={handleFlatSelect}
									renderOptionIcon={renderOptionIcon}
								/>
							))}
						</Section>
					</MenuGroup>
				</Box>
				{footer}
			</Box>
			{showFlyout && activeOption ? (
				<RemixSubTypeColumn
					key={activeType}
					subTypes={activeSubTypes}
					categoryOffset={categoryOffset}
					pickForMeTestId={`${activeOption.testId}-pick-for-me`}
					onSubTypeSelect={handleSubTypeSelect}
					onPickForMe={handlePickForMe}
					onClearPreview={handleClearPreview}
					onHoverSubType={handleHoverSubType}
				/>
			) : null}
			{showFlyout && hoveredSubTypeDef ? (
				<RemixPreviewColumn
					subType={hoveredSubTypeDef}
					direction={flyoutDirection}
					previewOffset={previewOffset}
				/>
			) : null}
		</Box>
	);
}
